

import { useState, useEffect, useMemo, useCallback } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import RouteErrorBoundary from "components/RouteErrorBoundary";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Sidenav from "examples/Sidenav";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import themeV2 from "assets/theme-v2";
import useUiVersion from "hooks/useUiVersion";
import AppFrame from "shell-v2/AppFrame";
import { UI_V2, isUiV2Enabled, readStoredUiVersion, resolveUiVersion } from "hooks/uiVersion";
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";
import routes from "routes";
import { buildSidenavRoutes, mergeCustomObjectsIntoPrefs, TRAVEL_GATED_KEYS } from "sidenavCatalog";
import { filterRoutesForRouter, withoutHiddenModules } from "moduleEntitlementMap";
import { ModuleEntitlementsProvider, useModuleEntitlements } from "./hooks/useModuleEntitlements";
import { useFeatureLicense } from "./hooks/useFeatureLicense";
import { useMaterialUIController, setMiniSidenav } from "context";
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import { AuthProvider, useAuth } from "./auth/Auth";
import PermissionsProvider, { usePermissionsContext } from "./auth/PermissionsProvider";
import { fetchCompanyInfo, invalidateCompanyInfo } from "./utils/companyInfo";
import { SettingsProvider } from "./context/SettingsContext";
import SignIn from "./layouts/authentication/sign-in";
import TenantLocked from "./layouts/authentication/TenantLocked";
import SubscriptionBanner from "components/SubscriptionBanner";
import ProtectedRoute from "./auth/ProtectedRoute";
import { WebSocketProvider } from "context/WebSocketContext";
import { TelephonyProvider } from "telephony/TelephonyContext";
import SoftphoneDock from "components/Softphone/SoftphoneDock";
import { AiAssistantProvider } from "context/AiAssistantContext";
import AiAssistantPanel from "components/AiAssistant/AiAssistantPanel";
import { CurrencyProvider } from "./context/CurrencyContext";
import WebhookProxy from "./components/WebhookProxy";
import PublicPayPage from "./layouts/public/PublicPayPage";
import MerchantAllocationRespond from "./layouts/public/MerchantAllocationRespond";
import MerchantOrderLineRespond from "./layouts/public/MerchantOrderLineRespond";
import { installCssZoomOverlayFix } from "assets/theme/base/cssZoomOverlays";

// body { zoom: 0.8 } breaks MUI Popover top/left (Selects, Menus). Correct once.
// UI v2 renders without the zoom, so it skips the patch; AppContent installs
// it later if the server turns out to say legacy after all. It is idempotent.
// The sign-in page is always drawn in the v2 look, so it skips it too.
const LOGIN_PATH = "/auth/login";
if (
  !window.location.pathname.startsWith(LOGIN_PATH) &&
  resolveUiVersion({ stored: readStoredUiVersion(), enabled: isUiV2Enabled() }) !== UI_V2
) {
  installCssZoomOverlayFix();
}


// Real Estate pages that exist in the router but are reached from a list or a
// button, never from the sidenav: detail pages and the booking wizard.
const RE_SIDENAV_HIDDEN_KEYS = new Set([
  "re-project-detail",
  "re-developer-detail",
  "re-building-detail",
  "re-unit-type-detail",
  "re-unit-detail",
  "re-price-list-detail",
  "re-viewing-detail",
  "re-booking-new",
  "re-booking-detail",
  "re-payment-plan-detail",
  "re-broker-firm-detail",
  "re-broker-agent-detail",
  "re-listing-detail",
  "re-lease-detail",
  "re-mortgage-application-detail",
]);

// Match exact path or a nested path under it ("/pay" must not match "/payment-templates").
const matchesPathPrefix = (pathname, route) =>
  pathname === route || pathname.startsWith(`${route}/`);

// Add this helper function to check if a route should be protected
const isProtectedRoute = (path) => {
  const unprotectedRoutes = [
    '/auth/login',
    '/reset-password',
    '/create-password',
    '/pay',
    '/merchant-allocations/respond',
    '/merchant-order-lines/respond',
  ];
  return !unprotectedRoutes.some((route) => matchesPathPrefix(path, route));
};

// Add this helper to check if sidebar should be shown
const shouldShowSidebarForRoute = (pathname) => {
  const noSidebarRoutes = [
    '/auth/login',
    // Signed in, but a decision screen for an OAuth app, not a page of the app.
    '/oauth/consent',
    '/reset-password',
    '/create-password',
    '/pay',
    '/merchant-allocations/respond',
    '/merchant-order-lines/respond',
  ];
  return !noSidebarRoutes.some((route) => matchesPathPrefix(pathname, route));
};

// Create a Protected Layout component (CurrencyProvider is already at App root)
const ProtectedLayout = ({ children }) => {
  return children;
};

function AppContent() {
  const {
    user,
    token,
    axiosInstance,
    isAuthReady,
    isAuthenticated,
    tenantLockout,
  } = useAuth();
  const {
    enabledModuleCodes,
    enabledEditions,
    hasTravelEntitlement,
    catalog: moduleCatalog,
    reHiddenModules,
    subscription: entitlementSubscription,
    loading: entitlementsLoading,
  } = useModuleEntitlements();
  const { features, loading: featureLicensesLoading } = useFeatureLicense();
  const permissions = usePermissionsContext();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const { isV2 } = useUiVersion();
  const { pathname: lookPath } = useLocation();
  // Nobody's UI version is known before sign-in; that page is always v2.
  const v2Look = isV2 || matchesPathPrefix(lookPath, LOGIN_PATH);
  // v2 is light only; the legacy dark toggle does not apply to it.
  const activeTheme = v2Look ? themeV2 : darkMode ? themeDark : theme;

  useEffect(() => {
    document.body.classList.toggle("ui-v2", v2Look);
    if (v2Look) {
      // ZoomControls may have left an inline zoom from a legacy session.
      document.body.style.removeProperty("zoom");
    } else {
      installCssZoomOverlayFix();
    }
  }, [v2Look]);
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const [isTravelEnabled, setIsTravelEnabled] = useState(false);
  const [sidenavPreferences, setSidenavPreferences] = useState(null);

  // Fetch company info to check if travel is enabled
  useEffect(() => {
    const fetchTravelStatus = async () => {
      try {
        if (!isAuthReady || !isAuthenticated) return;

        const data = await fetchCompanyInfo();
        if (data) {
          // Check is_travel_enabled boolean field (API may return bool or string)
          setIsTravelEnabled(
            data.is_travel_enabled === true || data.is_travel_enabled === 'true'
          );
        }
      } catch (error) {
        console.error('Error fetching travel status:', error);
      }
    };

    fetchTravelStatus();

    const handleAuthTokenUpdated = () => {
      // A new token can mean a different tenant — drop the cached payload.
      invalidateCompanyInfo();
      fetchTravelStatus();
    };

    window.addEventListener('auth-token-updated', handleAuthTokenUpdated);

    // Listen for travel feature updates from Settings toggle
    const handleTravelUpdate = (event) => {
      const enabled = event?.detail?.enabled === true;
      invalidateCompanyInfo();
      setIsTravelEnabled(enabled);
      // Older saved sidenav prefs may omit Travel; inject and unhide when enabling.
      if (enabled) {
        setSidenavPreferences((prev) => {
          const { prefs } = mergeCustomObjectsIntoPrefs(prev, null, moduleCatalog);
          return {
            ...prefs,
            sections: (prefs.sections || []).map((section) => ({
              ...section,
              items: (section.items || []).map((item) =>
                TRAVEL_GATED_KEYS.has(item.id) ? { ...item, hidden: false } : item
              ),
            })),
          };
        });
      }
    };

    window.addEventListener('travelFeatureUpdated', handleTravelUpdate);
    return () => {
      window.removeEventListener('travelFeatureUpdated', handleTravelUpdate);
      window.removeEventListener('auth-token-updated', handleAuthTokenUpdated);
    };
  }, [isAuthReady, isAuthenticated, token]);

  const loadSidenavPreferences = useCallback(async () => {
    if (!isAuthReady || !isAuthenticated || !token || !axiosInstance) {
      setSidenavPreferences(null);
      return;
    }
    try {
      // These two are independent — request them together rather than
      // waiting for preferences to come back before starting custom-objects.
      const [prefsRes, objectsRes] = await Promise.all([
        axiosInstance.get("/users/me/preferences"),
        features.custom_objects
          ? axiosInstance.get("/custom-objects").catch(() => null)
          : Promise.resolve(null),
      ]);

      const prefs = prefsRes.data?.sidenav_preferences || null;

      const customObjects = objectsRes
        ? Array.isArray(objectsRes.data)
          ? objectsRes.data
          : objectsRes.data?.items || objectsRes.data?.objects || []
        : null;

      const { prefs: merged } = mergeCustomObjectsIntoPrefs(prefs, customObjects, moduleCatalog);
      setSidenavPreferences(merged);
    } catch (error) {
      console.error("Error fetching sidenav preferences:", error);
      setSidenavPreferences(null);
    }
  }, [
    axiosInstance,
    features.custom_objects,
    isAuthReady,
    isAuthenticated,
    token,
    // Re-merge once the live catalog arrives so an add-on sold after this
    // layout was saved gets its sidenav item without a reload.
    moduleCatalog,
  ]);

  useEffect(() => {
    loadSidenavPreferences();

    const handlePrefsUpdated = (event) => {
      if (event?.detail) {
        const customObjects = event?.detail?.customObjects;
        if (Array.isArray(customObjects)) {
          const { prefs: merged } = mergeCustomObjectsIntoPrefs(event.detail, customObjects, moduleCatalog);
          setSidenavPreferences(merged);
        } else {
          setSidenavPreferences(event.detail);
        }
      } else {
        loadSidenavPreferences();
      }
    };

    window.addEventListener("sidenavPreferencesUpdated", handlePrefsUpdated);
    return () => window.removeEventListener("sidenavPreferencesUpdated", handlePrefsUpdated);
  }, [loadSidenavPreferences]);

  // Travel pages need the TRAVEL edition and the company flag. System admins
  // used to bypass the entitlement half, which put Travel in front of tenants
  // that never bought it (a Real Estate tenant cloned from a template with the
  // flag set). Settings can still show the toggle to them; the navigation
  // follows the entitlement.
  const effectiveTravelEntitlement = hasTravelEntitlement;

  const accessOptions = useMemo(
    () => ({
      enabledModuleCodes,
      // Edition modules (Real Estate, Travel) also need their edition.
      enabledEditions,
      hasTravelEntitlement: effectiveTravelEntitlement,
      isTravelEnabled: effectiveTravelEntitlement && isTravelEnabled,
      isCustomObjectsEnabled: Boolean(features.custom_objects),
      entitlementsReady: !entitlementsLoading && !featureLicensesLoading,
      // The live registry catalog; the snapshot applies while null.
      moduleCatalog,
      // Object permissions. No-ops unless the tenant is enforcing AND the
      // snapshot has loaded, so this cannot blank the navigation.
      permissionMode: permissions.mode,
      permissionsReady: permissions.ready,
      allowedRouteKeys: permissions.allowedRouteKeys,
      governedRouteKeys: permissions.governedRouteKeys,
      // v2 keeps standard Products / Quotations / Orders with travel on (the
      // Sales app owns them); classic hides them as before.
      isV2,
    }),
    [
      enabledModuleCodes,
      enabledEditions,
      entitlementsLoading,
      moduleCatalog,
      effectiveTravelEntitlement,
      isTravelEnabled,
      features.custom_objects,
      featureLicensesLoading,
      permissions.mode,
      permissions.ready,
      permissions.allowedRouteKeys,
      permissions.governedRouteKeys,
      isV2,
    ]
  );

  const entitlementFilteredRoutes = useMemo(
    () => filterRoutesForRouter(routes, accessOptions),
    [accessOptions]
  );

  // Filter routes for sidenav display (hide detail pages and apply role rules)
  const filterRoutesForSidenav = (routesArray) => {
    // First apply the standard filtering
    const baseFiltered = routesArray.filter(route =>
      route.key !== "account-detail" &&
      route.key !== "oauth-consent" &&
      route.key !== "connected-apps" &&
      route.key !== "lead-detail" &&
      route.key !== "user-management" &&
      route.key !== "deals-detail" &&
      route.key !== "order-detail" &&
      route.key !== "quotation-detail" &&
      route.key !== "email-setup" &&
      route.key !== "settings" &&
      // Telephony config lives under Settings, not the sidenav.
      route.key !== "call-detail" &&
      route.key !== "telephony-settings" &&
      route.key !== "telephony-setup" &&
      route.key !== "telephony-numbers" &&
      route.key !== "telephony-routing" &&
      route.key !== "telephony-recording" &&
      route.key !== "telephony-ivr" &&
      // Queue configuration is a Settings page too. work-queues and
      // agent-workload are deliberately NOT here -- those are agent-facing and
      // belong in the sidenav.
      route.key !== "queues-settings" &&
      route.key !== "queue-capacity-settings" &&
      route.key !== "skills-settings" &&
      route.key !== "messages" &&
      route.key !== "custom-module" &&
      route.key !== "product-detail" &&
      route.key !== "contact-detail" &&
      route.key !== "gmail-auth-callback" &&
      route.key !== "profile" &&
      route.key !== "agent-map" &&
      route.key !== "search" &&
      route.key !== "create-password" &&
      route.key !== "case-detail" &&
      route.key !== "quotation" &&
      route.key !== "dashboard-detail" &&
      route.key !== "report-new" &&
      route.key !== "report-edit" &&
      route.key !== "report-detail" &&
      route.key !== "reset-password" &&
      route.key !== "customer-360-view" &&
      route.key !== "support-ticket" &&
      route.key !== "view-user-profile" &&
      route.key !== "invoice-detail" &&
      route.key !== "automation-builder" &&
      route.key !== "custom-object-list" &&
      route.key !== "custom-object-detail" &&
      route.key !== "trips" &&
      route.key !== "cms-publisher" &&
      // Keep payment-templates / payment-transactions visible under Travel sidenav
      route.key !== "trip-options" &&
      route.key !== "guide-allocations" &&
      route.key !== "creator-onboardings" &&
      route.key !== "creators" &&
      route.key !== "trip-sub-options" &&
      route.key !== "departure-dates" &&
      route.key !== "addons" &&
      route.key !== "faqs" &&
      route.key !== "payment-template-detail" &&
      route.key !== "trip-sub-option-detail" &&
      route.key !== "departure-date-detail" &&
      route.key !== "addon-detail" &&
      route.key !== "faq-detail" &&
      route.key !== "trip-detail" &&
      route.key !== "cms-publisher-detail" &&
      route.key !== "trip-option-detail" &&
      route.key !== "itineraries" &&
      route.key !== "itinerary-detail" &&
      route.key !== "itinerary-days" &&
      route.key !== "guide-allocation-detail" &&
      route.key !== "creator-onboarding-detail" &&
      route.key !== "creator-detail" &&
      route.key !== "itinerary-day-detail" &&
      route.key !== "itinerary-day-line-items" &&
      route.key !== "itinerary-day-line-item-detail" &&
      route.key !== "day-line-items" &&
      route.key !== "day-line-item-detail" &&
      route.key !== "trip-components" &&
      route.key !== "trip-component-detail" &&
      route.key !== "merchant-assets" &&
      route.key !== "merchant-asset-detail" &&
      route.key !== "training-modules" &&
      route.key !== "training-module-detail" &&
      route.key !== "training-completions" &&
      route.key !== "training-completion-detail" &&
      route.key !== "payment-terms" &&
      route.key !== "payment-term-detail" &&
      route.key !== "merchant-asset-inventories" &&
      route.key !== "merchant-asset-inventory-detail" &&
      route.key !== "merchant-allocations" &&
      route.key !== "merchant-allocations-detail" &&
      route.key !== "merchant-allocation-detail" &&
      route.key !== "travel-order-create" &&
      // Hide new travel module routes
      route.key !== "travel-order-detail" &&
      route.key !== "insurances" &&
      route.key !== "insurance-detail" &&
      route.key !== "travel-order-items" &&
      route.key !== "travel-order-item-detail" &&
      route.key !== "travel-order-pax" &&
      route.key !== "travel-order-pax-detail" &&
      route.key !== "travel-order-item-pax" &&
      route.key !== "travel-order-item-pax-detail" &&
      route.key !== "order-itineraries" &&
      route.key !== "order-itinerary-detail" &&
      route.key !== "merchant-order-lines" &&
      route.key !== "merchant-order-line-detail" &&
      route.key !== "merchant-order-line-respond" &&
      route.key !== "order-coupons" &&
      route.key !== "order-coupon-detail" &&
      route.key !== "order-comms" &&
      route.key !== "order-comm-detail" &&
      route.key !== "coupons" &&
      route.key !== "coupon-detail" &&
      // Keep payment-transactions visible under Travel sidenav (detail stays hidden)
      route.key !== "payment-transaction-detail" &&
      route.key !== "order-cancellations" &&
      route.key !== "order-cancellation-detail" &&
      route.key !== "merchants" &&
      route.key !== "merchant-detail" &&
      route.key !== "travel-contacts" &&
      route.key !== "travel-contact-detail" &&
      route.key !== "opportunities" &&
      route.key !== "opportunity-detail" &&
      route.key !== "customers" &&
      route.key !== "customer-detail" &&
      route.key !== "pricing-categories" &&
      route.key !== "pricing-category-detail" &&
      route.key !== "customer-coupons" &&
      route.key !== "customer-coupon-detail" &&
      route.key !== "loyalty-rules" &&
      route.key !== "loyalty-rule-detail" &&
      route.key !== "loyalty-ledgers" &&
      route.key !== "loyalty-ledger-detail" &&
      route.key !== "loyalty-summaries" &&
      route.key !== "loyalty-summary-detail" &&
      route.key !== "loyalty-tiers" &&
      route.key !== "loyalty-tier-detail" &&
      route.key !== "referral-redemptions" &&
      route.key !== "referral-redemption-detail" &&
      route.key !== "referral-program-settings" &&
      route.key !== "referral-program-setting-detail" &&
      route.key !== "guide-onboardings" &&
      route.key !== "guide-onboarding-detail" &&
      // Hide new invoice and referral routes
      route.key !== "guide-trip-notes" &&
      route.key !== "guide-trip-note-detail" &&
      route.key !== "referral-codes" &&
      route.key !== "referral-code-detail" &&
      route.key !== "travel-invoices" &&
      route.key !== "travel-invoice-detail" &&
      route.key !== "travel-invoice-line-items" &&
      route.key !== "travel-invoice-line-item-detail" &&
      !RE_SIDENAV_HIDDEN_KEYS.has(route.key)
    );

    // Now filter based on user role - hide Reports for standard_user
    const isStandardUser = user && user.role && user.role.toLowerCase() === "standard_user";

    // Reports used to be hidden from every standard user by their role name.
    // It is now a registered object in the permission registry, so a grant
    // decides — which is what lets an admin give one rep access to reports
    // without promoting them to manager. The route-key filter in
    // filterRoutesForRouter already applies that grant.
    return baseFiltered;
  };

  const filteredRoutes = useMemo(() => {
    // Real Estate personas take their modules' pages out of navigation only.
    return withoutHiddenModules(
      filterRoutesForSidenav(entitlementFilteredRoutes),
      reHiddenModules,
      moduleCatalog
    );
  }, [user, entitlementFilteredRoutes, reHiddenModules, moduleCatalog]);

  const sidenavRoutes = useMemo(() => {
    return buildSidenavRoutes(filteredRoutes, sidenavPreferences, {
      isTravelEnabled: accessOptions.isTravelEnabled,
      hasTravelEntitlement: accessOptions.hasTravelEntitlement,
      role: user?.role,
      enabledModuleCodes,
      enabledEditions,
      isCustomObjectsEnabled: accessOptions.isCustomObjectsEnabled,
      entitlementsReady: accessOptions.entitlementsReady,
      moduleCatalog: accessOptions.moduleCatalog,
      isV2,
    });
  }, [filteredRoutes, sidenavPreferences, accessOptions, enabledModuleCodes, enabledEditions, user, isV2]);

  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getProtectedRoutes = (allRoutes) =>
    allRoutes.flatMap((route) => {
      if (route.collapse) {
        return getProtectedRoutes(route.collapse);
      }

      if (route.route && isProtectedRoute(route.route)) {
        return [
          <Route
            exact
            path={route.route}
            element={
              <ProtectedRoute>
                <ProtectedLayout>
                  {route.component}
                </ProtectedLayout>
              </ProtectedRoute>
            }
            key={route.key}
          />,
        ];
      }
      return [];
    });

  const getPublicRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getPublicRoutes(route.collapse);
      }

      if (route.route && !isProtectedRoute(route.route)) {
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key}
          />
        );
      }
      return null;
    }).filter(Boolean);

  // Update sidebar visibility check
  const shouldShowSidebar =
    layout === "dashboard" &&
    shouldShowSidebarForRoute(pathname);

  // Prefer the permissions snapshot; fall back to the entitlements response
  // for tenants in legacy permission mode, which never build a snapshot.
  const subscriptionState = permissions?.subscription ?? entitlementSubscription;

  // A locked-out workspace replaces the application rather than routing to a
  // page: there is nothing here the user can usefully do, and leaving the
  // shell up would fire a wave of requests that all come back 403.
  // Only for someone who was working and got locked out mid-session. On a
  // public route -- above all /auth/login -- this screen would replace the
  // form they need to sign in with a different account, and the only way
  // out would be the Sign out button. The login page shows the same reason
  // inline instead.
  if (tenantLockout && isProtectedRoute(pathname)) {
    return (
      <ThemeProvider theme={activeTheme}>
        <CssBaseline />
        <TenantLocked lockout={tenantLockout} />
      </ThemeProvider>
    );
  }

  // The banner and the routed page. Classic renders them beside the Sidenav;
  // v2 renders them inside AppFrame's scrolling <main>.
  const pageTree = (
    <>
    {/* A render throw used to unmount the whole app and leave a white
        page. Inside the router so navigation survives, and keyed on
        pathname so moving elsewhere clears it. */}
    {/* Inside the shell so it sits above page content, and only
        where the shell is shown -- a grace warning on the public
        payment page would be addressed to the wrong person. */}
    {shouldShowSidebar && <SubscriptionBanner subscription={subscriptionState} />}
    <RouteErrorBoundary resetKey={pathname}>
    <Routes>
      {/* Public routes without CurrencyProvider */}
      <Route path={LOGIN_PATH} element={<SignIn />} />
      <Route path="/pay/:token" element={<PublicPayPage />} />
      <Route path="/merchant-allocations/respond/:token" element={<MerchantAllocationRespond />} />
      <Route path="/merchant-order-lines/respond/:token" element={<MerchantOrderLineRespond />} />
      {getPublicRoutes(entitlementFilteredRoutes)}

      {/* Protected routes with CurrencyProvider */}
      {getProtectedRoutes(entitlementFilteredRoutes)}
      {/* /reports is declared in routes.js and rendered by
          getProtectedRoutes above, so it passes through entitlement
          and permission filtering. It used to ALSO be hand-written
          here, which meant that a tenant or user denied the reports
          route still got the page: the filtered copy disappeared and
          this unfiltered one caught the path instead. */}

      {/* Default route */}
      <Route path="/" element={<Navigate to="/auth/login" />} />
    </Routes>
    </RouteErrorBoundary>

    </>
  );

  return (
    <WebSocketProvider>
        <SettingsProvider>
          {/* Above <Routes> so a live call survives navigation, and always
              rendered rather than conditionally -- a provider that unmounts
              when telephony is unavailable would change hook order. It stays
              inert until telephony is entitled and configured. */}
          <TelephonyProvider>
          {/* Same reasoning: always mounted, inert until the user holds an
              AI Assistant seat and the organisation has credits. */}
          <AiAssistantProvider>
          <ThemeProvider theme={activeTheme}>
            <CssBaseline />

            {shouldShowSidebar && !isV2 && (
              <Sidenav
                color={sidenavColor}
                brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                brandName="PearlCrm"
                routes={sidenavRoutes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
            )}
            {shouldShowSidebar && isV2 ? (
              <AppFrame sidenavRoutes={sidenavRoutes} allowedRoutes={entitlementFilteredRoutes}>
                {pageTree}
              </AppFrame>
            ) : (
              pageTree
            )}

            {/* Sibling of <Routes>, gated like the Sidenav so it never appears
                on login or the public payment page. */}
            {/* Classic only: v2 shows the calling bar inside AppFrame instead. */}
            {shouldShowSidebar && !isV2 && <SoftphoneDock />}
            {shouldShowSidebar && <AiAssistantPanel />}
          </ThemeProvider>
          </AiAssistantProvider>
          </TelephonyProvider>
        </SettingsProvider>
      </WebSocketProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      {/* Inside AuthProvider: it needs the axios instance and the ready flags.
          Above AppContent: route filtering consumes the snapshot. */}
      <PermissionsProvider>
        {/* One /module-entitlements fetch for the whole tree; the sixteen
            callers of useModuleEntitlements read this instead of each
            fetching their own. */}
        <ModuleEntitlementsProvider>
          <CurrencyProvider>
            <AppContent />
          </CurrencyProvider>
        </ModuleEntitlementsProvider>
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;
